import { useCallback, useEffect, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PageHead, Stat, Empty, selectCls, btnPrimary, btnGhost, qtyFmt } from "@/components/erp/ErpUi";
import { toast } from "sonner";
import { PackagePlus, Loader2, ShoppingCart } from "lucide-react";

export default function OwnerSuggestions() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const nav = useNavigate();
  const load = useCallback(() => api.get("/erp/purchase-suggestions", { params: { days } })
    .then(({ data }) => { setData(data); setSel(Object.fromEntries(data.items.map((i) => [i.product_id, { qty: i.suggested_qty, unit_cost: i.last_supplier?.unit_cost || 0 }]))); })
    .catch((e) => toast.error(formatApiError(e))), [days]);
  useEffect(() => { load(); }, [load]);
  const bySupplier = (data?.items || []).reduce((m, it) => {
    const key = it.last_supplier?.supplier_id || "_sem";
    (m[key] = m[key] || { supplier: it.last_supplier?.supplier_name || "Sem fornecedor registrado", id: it.last_supplier?.supplier_id, items: [] }).items.push(it);
    return m;
  }, {});
  const goPurchase = (sup) => {
    const items = sup.items.map((it) => ({
      product_id: it.product_id, qty: Number(sel[it.product_id]?.qty || it.suggested_qty) || 1,
      unit_cost: Number(sel[it.product_id]?.unit_cost || it.last_supplier?.unit_cost || 0),
    }));
    sessionStorage.setItem("purchase_draft", JSON.stringify({ supplier_id: sup.id, items }));
    toast.success("Pedido pronto na tela de Compras");
    nav("/owner/compras?draft=1");
  };
  return (
    <div className="space-y-5 animate-fade-up">
      <PageHead icon={PackagePlus} kicker="Estoque" title="Sugestão de compra">
        <label className="text-xs text-zinc-400 inline-flex items-center gap-2">Período
          <select data-testid="suggest-days" value={days} onChange={(e) => setDays(Number(e.target.value))} className={`${selectCls} w-auto`}>
            {[30, 60, 90, 120, 180].map((d) => <option key={d} value={d}>Últimos {d} dias</option>)}
          </select></label>
      </PageHead>
      <p className="text-xs text-zinc-500 max-w-2xl">Lista peças abaixo do mínimo ou que vão acabar nos próximos 30 dias, pela média de saída do período. A quantidade sugerida cobre cerca de 60 dias.</p>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Stat testid="suggest-count" label="Peças sugeridas" value={data?.count ?? "…"} tone="amber" />
        <Stat testid="suggest-days-stat" label="Base de cálculo" value={data ? `${data.days} dias` : "…"} />
        <Stat testid="suggest-groups" label="Fornecedores" value={Object.keys(bySupplier).length || "…"} />
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" />
        : data.items.length === 0 ? <Empty>Nenhuma peça precisa de reposição.</Empty>
        : Object.values(bySupplier).map((sup) => (
          <div key={sup.id || sup.supplier} data-testid={`suggest-sup-${sup.id || "none"}`} className="rounded-2xl border border-zinc-800 bg-zinc-950/60">
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
              <div>
                <div className="text-[11px] font-mono-caps text-zinc-500">Fornecedor</div>
                <div className="font-display text-lg font-bold text-zinc-100">{sup.supplier}</div>
              </div>
              <Button data-testid={`suggest-create-${sup.id || "none"}`} className={btnPrimary} onClick={() => goPurchase(sup)}>
                <ShoppingCart className="h-4 w-4 mr-1" /> Gerar pedido ({sup.items.length})
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead><tr className="bg-zinc-900 text-[11px] font-mono-caps text-zinc-500 uppercase">
                  <th className="text-left px-3 py-2">Peça</th><th className="text-right px-3 py-2">Estoque</th>
                  <th className="text-right px-3 py-2">Mínimo</th><th className="text-right px-3 py-2">Saída/dia</th>
                  <th className="text-right px-3 py-2">Dias restantes</th><th className="text-right px-3 py-2">Qtd. a comprar</th>
                  <th className="text-right px-3 py-2">Custo unit.</th>
                </tr></thead>
                <tbody>{sup.items.map((it) => (
                  <tr key={it.product_id} data-testid={`suggest-row-${it.product_id}`} className="border-t border-zinc-800">
                    <td className="px-3 py-2 text-zinc-100">
                      <Link to={`/owner/estoque`} className="hover:text-green-neon">{it.description}</Link>
                      <div className="text-[11px] text-zinc-500">{[it.code, it.brand].filter(Boolean).join(" · ") || "—"}</div>
                    </td>
                    <td className={`px-3 py-2 text-right ${it.stock <= 0 ? "text-red-300" : "text-zinc-300"}`}>{qtyFmt(it.stock)} {it.unit}</td>
                    <td className="px-3 py-2 text-right text-zinc-400">{qtyFmt(it.min_stock)}</td>
                    <td className="px-3 py-2 text-right text-zinc-300">{it.avg_per_day.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-zinc-300">{it.days_left == null ? "—" : it.days_left}</td>
                    <td className="px-3 py-2 text-right">
                      <input data-testid={`suggest-qty-${it.product_id}`} type="number" min="1" className="w-20 bg-zinc-900 border border-zinc-800 text-right rounded-md px-2 py-1 text-sm"
                             value={sel[it.product_id]?.qty ?? it.suggested_qty}
                             onChange={(e) => setSel((p) => ({ ...p, [it.product_id]: { ...(p[it.product_id] || {}), qty: e.target.value } }))} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input data-testid={`suggest-cost-${it.product_id}`} type="number" step="0.01" className="w-24 bg-zinc-900 border border-zinc-800 text-right rounded-md px-2 py-1 text-sm"
                             value={sel[it.product_id]?.unit_cost ?? (it.last_supplier?.unit_cost || 0)}
                             onChange={(e) => setSel((p) => ({ ...p, [it.product_id]: { ...(p[it.product_id] || {}), unit_cost: e.target.value } }))} />
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        ))}
    </div>
  );
}
